/**
 * ReviewSession — повторення карток.
 *
 * Два режими:
 *  • srs  — інтервальне повторення (FSRS-4.5 / SM-2). Оцінка «Сложно / Нормально /
 *    Идеально» перераховує інтервал і пише стан у D1 (reviews).
 *  • free — вільний режим: картки просто показуються, у review_log пишеться факт
 *    перегляду, але стан картки НЕ змінюється.
 *
 * Обидва режими живуть в одному списку; перемикач — нагорі. Неправильна оцінка
 * можна скасувати (кнопка «Скасувати» повертає попередній стан із буфера).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Check,
  Coffee,
  Flame,
  Layers,
  PartyPopper,
  RotateCcw,
  Shuffle,
  Target,
} from 'lucide-react'

import CardViewer from './CardViewer.jsx'
import { Segmented, Alert, Empty, Loading, Progress, Badge, TagPill } from './ui.jsx'
import { api } from '../lib/api.js'
import { SUBJECTS } from '../lib/constants.js'
import { normalizeState, schedule, formatInterval } from '../lib/scheduler.js'

const MODES = [
  { value: 'srs', label: 'Повторення' },
  { value: 'free', label: 'Вільний режим' },
]

export default function ReviewSession({ settings, decks = [], subject = 'all', deckId = null, onFinished, onNeedCards }) {
  const [mode, setMode] = useState('srs')
  const [queue, setQueue] = useState([])
  const [index, setIndex] = useState(0)
  const [revealed, setRevealed] = useState(false)
  const [hintVisible, setHintVisible] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [history, setHistory] = useState([])
  const [finished, setFinished] = useState(false)
  const [availableTags, setAvailableTags] = useState([])
  const [scope, setScope] = useState({
    subject: subject === 'all' ? 'all' : subject,
    deckId: deckId ?? null,
    tags: [],
    size: settings?.sessionSize ?? 20,
  })
  const [localStates, setLocalStates] = useState({})

  // Предмет із шапки — стартове значення області, а не захардкожена математика.
  useEffect(() => {
    setScope((s) => ({ ...s, subject }))
  }, [subject])

  const current = queue[index] ?? null
  const abortRef = useRef(null)
  const scopeDeck = decks.find((d) => d.id === scope.deckId) ?? null

  const load = useCallback(async () => {
    abortRef.current?.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl

    setLoading(true)
    setError(null)
    setFinished(false)
    setHistory([])
    setIndex(0)
    setRevealed(false)
    setHintVisible(false)
    setLocalStates({})

    try {
      const cards = await api.dueCards(
        {
          due: mode === 'srs' ? 'now' : undefined,
          subject: scope.subject === 'all' ? undefined : scope.subject,
          deckId: scope.deckId || undefined,
          tag: scope.tags.length ? scope.tags : undefined,
          limit: Math.min(Number(scope.size) || 20, 200),
        },
        { signal: ctrl.signal },
      )
      setQueue(cards)
    } catch (err) {
      if (err?.status !== 0) setError(err?.message ?? 'Не вдалося завантажити картки для повторення.')
    } finally {
      setLoading(false)
    }
  }, [mode, scope])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    api.listTags().then(setAvailableTags).catch(() => {})
  }, [])

  // Підказки від планувальника рахуємо на клієнті: так видно інтервал до натискання.
  const plan = useMemo(() => {
    if (!current || mode !== 'srs') return null
    const state = localStates[current.id] ?? current
    return schedule(normalizeState(state), 'normal', {
      algorithm: settings?.algorithm,
      desiredRetention: settings?.desiredRetention,
    })
  }, [current, mode, localStates, settings])

  const grade = useCallback(
    async (button) => {
      if (!current) return
      const before = current
      const state = normalizeState(localStates[before.id] ?? before)
      const result = schedule(state, button, {
        algorithm: settings?.algorithm,
        desiredRetention: settings?.desiredRetention,
      })

      setLocalStates((prev) => ({ ...prev, [before.id]: result.next }))
      setHistory((prev) => [...prev, { card: before, prev: state, next: result.next, button, mode }])
      setRevealed(false)
      setHintVisible(false)

      // Стан надсилаємо плоским — так його очікує /api/reviews.
      // У вільному режимі reviews не чіпаємо узагалі: лише журнал.
      const payload = {
        cardId: before.id,
        grade: result.grade,
        mode,
        ...result.next,
        elapsedMs: 0,
      }

      if (mode === 'srs') {
        try {
          await api.gradeCard(payload)
        } catch (err) {
          setError(err?.message ?? 'Не вдалося зберегти оцінку. Інтервал у базі не змінився.')
        }
      } else {
        api.gradeCard(payload).catch(() => {})
      }

      if (index + 1 >= queue.length) setFinished(true)
      else setIndex((i) => i + 1)
    },
    [current, localStates, mode, index, queue.length, settings],
  )

  const undo = useCallback(async () => {
    const last = history[history.length - 1]
    if (!last) return
    setHistory((prev) => prev.slice(0, -1))
    setLocalStates((prev) => {
      const next = { ...prev }
      delete next[last.card.id]
      return next
    })
    setIndex((i) => Math.max(0, i - 1))
    setRevealed(true)
    setError(null)

    if (last.mode !== 'srs') return

    try {
      // Повертаємо стан ДО оцінки і просимо сервер прибрати рядок журналу.
      await api.gradeCard({
        cardId: last.card.id,
        mode: 'srs',
        undo: true,
        ...last.prev,
      })
    } catch {
      /* сервер не відкотив — локально все одно повернули */
    }
  }, [history])

  const skip = useCallback(() => {
    setRevealed(false)
    setHintVisible(false)
    if (index + 1 >= queue.length) setFinished(true)
    else setIndex((i) => i + 1)
  }, [index, queue.length])

  const toggleTag = (name) => {
    setScope((s) => ({
      ...s,
      tags: s.tags.includes(name) ? s.tags.filter((t) => t !== name) : [...s.tags, name],
    }))
  }

  const restart = useCallback(() => {
    setIndex(0)
    setRevealed(false)
    setHintVisible(false)
    setFinished(false)
    setHistory([])
    setLocalStates({})
    setQueue((q) => {
      const shuffled = [...q]
      for (let i = shuffled.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
      }
      return shuffled
    })
  }, [])

  // ── Спостереження ───────────────────────────────────────────────────────────
  const done = finished || (!current && !loading)
  const reviewedCount = history.length

  if (loading) return <Loading label="Збираю картки на повторення…" />

  if (error && !queue.length) {
    return (
      <Alert tone="error" title="Не вдалося почати сесію">
        {error}
        <button type="button" onClick={load} className="mt-2 block font-semibold underline">
          Спробувати ще раз
        </button>
      </Alert>
    )
  }

  return (
    <div className="review-workspace">
      {/* Налаштування сесії — окремий службовий блок, не частина картки. */}
      <details className="session-setup" open={queue.length === 0}>
        <summary><span><span className="section-kicker">Сесія</span><b>{mode === 'srs' ? 'Інтервальне повторення' : 'Вільний режим'}</b></span><span className="setup-summary">{scope.size} карток · {scope.subject === 'all' ? 'усі предмети' : scope.subject} · {scopeDeck ? scopeDeck.title : 'усі колоди'} · {scope.tags.length ? scope.tags.join(', ') : 'усі теги'}</span></summary>
        <div className="session-controls">
        <Segmented value={mode} onChange={setMode} options={MODES} />
        <div className="session-selects"><select
          className="input w-auto"
          value={scope.subject}
          onChange={(e) => setScope((s) => ({ ...s, subject: e.target.value }))}
        >
          <option value="all">Усі предмети</option>
          {SUBJECTS.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>

        <select
          className="input w-auto"
          value={scope.deckId ?? ''}
          onChange={(e) => setScope((s) => ({ ...s, deckId: e.target.value ? Number(e.target.value) : null }))}
        >
          <option value="">Усі колоди</option>
          {decks.map((d) => (
            <option key={d.id} value={d.id}>
              {d.title}
            </option>
          ))}
        </select>

        <label className="flex items-center gap-2 text-sm text-slate-500 dark:text-slate-400">
          <input
            type="number"
            min={5}
            max={200}
            value={scope.size}
            onChange={(e) => setScope((s) => ({ ...s, size: Number(e.target.value) || 20 }))}
            className="input w-20 tabular-nums"
          />
          карток
        </label></div>

        {availableTags.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className="text-xs text-slate-500 dark:text-slate-400">Теги:</span>
            {availableTags.slice(0, 12).map((t) => (
              <TagPill
                key={t.id}
                name={t.name}
                count={t.card_count}
                active={scope.tags.includes(t.name)}
                onClick={() => toggleTag(t.name)}
              />
            ))}
          </div>
        )}

        {queue.length > 1 && !done && (
          <button type="button" onClick={restart} className="btn-ghost" title="Перемішати">
            <Shuffle className="h-4 w-4" /> Перемішати
          </button>
        )}

        <div className="ml-auto flex items-center gap-2">
          {mode === 'srs' && (
            <Badge title="Алгоритм інтервалів">
              {settings?.algorithm === 'sm2' ? 'SM-2' : 'FSRS-4.5'}
            </Badge>
          )}
          {reviewedCount > 0 && (
            <Badge className="bg-brand-100 text-brand-800 dark:bg-brand-950/60 dark:text-brand-200">
              {reviewedCount} переглянуто
            </Badge>
          )}
        </div></div>
      </details>

      {error && <Alert tone="warn">{error}</Alert>}

      {queue.length > 0 && !done && <div className="review-progress"><span>Прогрес сесії</span><Progress value={index} max={queue.length} className="h-1" /><b>{index + 1} / {queue.length}</b></div>}

      {/* Порожня сесія */}
      {done && !queue.length && (
        <Empty
          icon={PartyPopper}
          title="Сьогодні все зроблено"
          action={
            <div className="flex gap-2">
              <button type="button" onClick={load} className="btn-outline">
                <RotateCcw className="h-4 w-4" /> Перевірити ще раз
              </button>
              <button type="button" onClick={onNeedCards} className="btn-primary">
                <Layers className="h-4 w-4" /> Додати картки
              </button>
            </div>
          }
        >
          {mode === 'srs'
            ? 'Усі картки, які були терміновані на повторення, вже переглянуто. Нова партія з’явиться завтра або можна додати карток.'
            : 'Немає жодної картки за цими фільтрами. Спробуйте інший предмет, колоду або теги.'}
        </Empty>
      )}

      {/* Завершено */}
      {done && queue.length > 0 && (
        <Empty
          icon={PartyPopper}
          title="Сесію завершено"
          action={
            <div className="flex gap-2">
              <button type="button" onClick={restart} className="btn-outline">
                <RotateCcw className="h-4 w-4" /> Ще раз
              </button>
              <button type="button" onClick={onFinished} className="btn-primary">
                <Check className="h-4 w-4" /> До карток
              </button>
            </div>
          }
        >
          Переглянуто {reviewedCount} із {queue.length}.{' '}
          {mode === 'srs' ? 'Інтервали перераховано й збережено в D1.' : 'У вільному режимі інтервали не змінювалися.'}
        </Empty>
      )}

      {/* Картка */}
      {!done && current && (
        <div className="review-stage"><CardViewer
          card={{ ...current, ...(localStates[current.id] ?? {}) }}
          mode={mode}
          revealed={revealed}
          onReveal={() => setRevealed(true)}
          onGrade={grade}
          onSkip={skip}
          onUndo={history.length ? undo : undefined}
          canUndo={history.length > 0}
          preview={plan?.preview}
          hintVisible={hintVisible}
          onToggleHint={() => setHintVisible((v) => !v)}
          settings={settings}
          position={index}
          total={queue.length}
        /></div>
      )}

      {/* Підсумки сесії */}
      {reviewedCount > 0 && !done && (
        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
          <Badge className="bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-200">
            <Flame className="h-3 w-3" /> складних: {history.filter((h) => h.button === 'hard').length}
          </Badge>
          <Badge className="bg-brand-100 text-brand-800 dark:bg-brand-950/60 dark:text-brand-200">
            <Target className="h-3 w-3" /> добре: {history.filter((h) => h.button === 'normal').length}
          </Badge>
          <Badge className="bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-200">
            <Check className="h-3 w-3" /> ідеально: {history.filter((h) => h.button === 'perfect').length}
          </Badge>
          {current && localStates[current.id]?.interval > 0 && (
            <span className="ml-auto">
              наступний показ цієї картки: {formatInterval(localStates[current.id].interval)}
            </span>
          )}
        </div>
      )}

      {queue.length > 0 && !done && mode === 'free' && (
        <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400">
          <Coffee className="h-3.5 w-3.5" />
          Вільний режим: картки не отримують інтервали, у базі лишається лише запис у журналі.
        </p>
      )}
    </div>
  )
}
